/**
 * Black Marble — IN-ENGINE CINEMATIC SCENE. The intro now renders the REAL
 * {@link CenotaphRenderer} (cenotaph.glb at world origin, RADIUS_KM = 70) on its
 * OWN renderer + bloom-free EffectComposer, replacing the generic faked sphere that
 * introMarble.ts used to paint. It runs before the gameplay scene exists, so it owns
 * a self-contained renderer/scene/camera/composer — and disposing it leaves NO trace
 * on the gameplay render path (which builds a fresh CenotaphRenderer with default
 * STONE_* lighting later).
 *
 * Renderer creation is guarded exactly like introMarble: a missing WebGL context
 * (jsdom / headless CI) leaves the scene DISABLED and every later call a silent no-op,
 * so the cinematic degrades to the DOM typography overlay alone.
 *
 * Composer chain (RESOLVED): `BokehPass → cinematic postFx → OutputPass`. NO
 * UnrealBloomPass — the one cold accent (the cyan rim crescent) is a Fresnel term in
 * the cenotaph material, never a glow pass. Everything reads the CineClock `t`.
 */
import * as THREE from 'three';
import type { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import type { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import type { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { AshMoteLayer, CenotaphRenderer } from '../render/cenotaph.js';
import {
  CINEMATIC_AMBIENT_COLOR,
  CINEMATIC_AMBIENT_INTENSITY,
  CINEMATIC_SUN_COLOR,
  CINEMATIC_SUN_INTENSITY,
  createCinematicComposer,
} from '../render/scene.js';
import {
  INTRO_HANDOFF_KEY,
  SUN_DIR,
  introAperture,
  introExposure,
  introGrain,
  introMaxBlur,
  introSunFlicker,
  introSunOpacity,
  sampleIntroCamera,
  sampleIntroCamera24fps,
} from './introTimeline.js';

/** BokehPass DOF uniforms we animate per beat (typed loosely by @types/three). */
type BokehUniforms = Record<'focus' | 'aperture' | 'maxblur', { value: number }>;

/** Build the cold additive sun-disc sprite texture (soft halo baked into the gradient). */
const buildSunSprite = (): THREE.Sprite => {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0.0, 'rgba(225,242,248,1)'); // core rgb(225,242,248)
    g.addColorStop(0.06, 'rgba(207,230,238,0.9)');
    g.addColorStop(0.4, 'rgba(207,230,238,0.18)');
    g.addColorStop(1.0, 'rgba(207,230,238,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.SpriteMaterial({
    map: tex,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: false,
    transparent: true,
  });
  const sprite = new THREE.Sprite(mat);
  // The VISIBLE cold sun is decoupled from the lighting `SUN_DIR` — placed up-and-front
  // (−Z) so it reads in the upper void of the Ignition/Emergence cameras (which look up
  // toward it). It fades out by the wide Marble (see introSunOpacity).
  sprite.position.copy(SUN_SPRITE_DIR).multiplyScalar(1.1e6);
  sprite.scale.setScalar(5.5e4); // small sharp disc + soft halo (reference 02-beat)
  return sprite;
};

/** Direction toward the visible cold-sun disc (screen upper-right of the opening beats). */
const SUN_SPRITE_DIR = new THREE.Vector3(0.2, 0.46, -0.86).normalize();

/** The thin raking sliver of cold light streaking off the sun across the opening void. */
const buildSliver = (): THREE.Sprite => {
  const w = 1024;
  const h = 64;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createLinearGradient(0, 0, w, 0);
    g.addColorStop(0.0, 'rgba(225,242,248,0)');
    g.addColorStop(0.5, 'rgba(235,248,252,0.9)');
    g.addColorStop(1.0, 'rgba(225,242,248,0)');
    ctx.fillStyle = g;
    ctx.globalAlpha = 0.4;
    ctx.fillRect(0, h * 0.5 - 9, w, 18); // soft wide glow
    ctx.globalAlpha = 1;
    ctx.fillRect(0, h * 0.5 - 2, w, 4); // bright hairline core
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.SpriteMaterial({
    map: tex,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: false,
    transparent: true,
    rotation: -0.32, // ~-18° dutch streak, matching the reference Ignition rake
  });
  const sprite = new THREE.Sprite(mat);
  sprite.position.copy(SUN_SPRITE_DIR).multiplyScalar(1.06e6);
  sprite.scale.set(7.2e5, 2.0e4, 1); // long + hairline-thin
  return sprite;
};

/** A soft round-dot sprite texture so GL points render as stars, not hard squares. */
const buildRoundPointTexture = (): THREE.CanvasTexture => {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0.0, 'rgba(255,255,255,1)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.85)');
    g.addColorStop(1.0, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

/** A sparse, cold starfield Points cloud on a far shell (deterministic, no Math.random). */
const buildStarfield = (): THREE.Points => {
  const count = 640;
  const positions = new Float32Array(count * 3);
  const shell = 9.0e5; // within the cinematic camera far plane (1.4e6)
  for (let i = 0; i < count; i++) {
    // deterministic spherical distribution from a hashed seed
    const u = (Math.sin(i * 12.9898) * 43758.5453) % 1;
    const v = (Math.sin(i * 78.233) * 24634.6345) % 1;
    const theta = Math.acos(2 * Math.abs(u) - 1);
    const phi = 2 * Math.PI * Math.abs(v);
    positions[i * 3] = shell * Math.sin(theta) * Math.cos(phi);
    positions[i * 3 + 1] = shell * Math.sin(theta) * Math.sin(phi);
    positions[i * 3 + 2] = shell * Math.cos(theta);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({
    color: 0xd2dde6, // cold near-white — the reference starfield reads clearly
    size: 3.2, // soft round sprite reads smaller than its texel box
    sizeAttenuation: false,
    map: buildRoundPointTexture(),
    alphaTest: 0.02,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
  });
  const pts = new THREE.Points(geo, mat);
  pts.frustumCulled = false;
  return pts;
};

export class IntroScene {
  /** Full-stage canvas the controller slots behind the DOM typography overlay. */
  readonly element: HTMLCanvasElement;
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private cenotaph: CenotaphRenderer | null = null;
  private ash: AshMoteLayer | null = null;
  private sunSprite: THREE.Sprite | null = null;
  private sliver: THREE.Sprite | null = null;
  private composer: EffectComposer | null = null;
  private bokeh: BokehPass | null = null;
  private postFx: ShaderPass | null = null;
  private readonly disposables: Array<{ dispose(): void }> = [];

  constructor() {
    this.element = document.createElement('canvas');
    this.element.className = 'ic-scene-canvas';
  }

  /** True once a real WebGL scene was built (false in headless / reduced-motion). */
  get enabled(): boolean {
    return this.renderer !== null;
  }

  /** Build the cinematic scene at `width`×`height`. No-op (disabled) without WebGL. */
  start(width: number, height: number): void {
    if (this.renderer) return;
    const renderer = this.createRenderer();
    if (!renderer) return; // jsdom / headless — degrade to the DOM overlay alone
    this.renderer = renderer;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = introExposure(0);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    this.scene = scene;

    // Tight cinematic depth range (km): the scene spans the 70 km monument out to the
    // ~9e5 km star shell. The gameplay 0.01 .. 1e12 range (no logarithmicDepthBuffer here)
    // crushed all geometry to NDC z≈0.9999 and made BokehPass depth degenerate — this
    // bracket keeps depth precise and the DOF sane.
    const camera = new THREE.PerspectiveCamera(33, width / Math.max(height, 1), 1, 1.4e6);
    this.camera = camera;
    sampleIntroCamera(camera, 0);

    // Cold key from up-and-right-and-behind the opening camera (silhouette → rim → graze).
    const sun = new THREE.DirectionalLight(CINEMATIC_SUN_COLOR.clone(), CINEMATIC_SUN_INTENSITY);
    sun.position.copy(SUN_DIR).multiplyScalar(6.0e6);
    sun.target.position.set(0, 0, 0);
    scene.add(sun, sun.target);
    scene.add(new THREE.AmbientLight(CINEMATIC_AMBIENT_COLOR, CINEMATIC_AMBIENT_INTENSITY));

    const stars = buildStarfield();
    scene.add(stars);
    this.disposables.push(stars.geometry, stars.material as THREE.Material);
    const sprite = buildSunSprite();
    this.sunSprite = sprite;
    scene.add(sprite);
    this.disposables.push(sprite.material);
    const sliver = buildSliver();
    this.sliver = sliver;
    scene.add(sliver);
    this.disposables.push(sliver.material);

    // The REAL monument — proxy renderable from frame 0; HQ GLB crossfades in async.
    const worldGroup = new THREE.Group();
    scene.add(worldGroup);
    this.cenotaph = new CenotaphRenderer(worldGroup);
    this.cenotaph.setCinematicMaterial(true);

    // Foreground paper-ash motes, parented to the camera (screen-space drift).
    this.ash = new AshMoteLayer();
    camera.add(this.ash.points);
    scene.add(camera);

    const cine = createCinematicComposer(renderer, scene, camera);
    this.composer = cine.composer;
    this.bokeh = cine.bokeh;
    this.postFx = cine.postFx;

    this.resize(width, height);
    this.warmUp(); // hidden first render so first-frame shader compile/upload doesn't hitch at t=0
  }

  private createRenderer(): THREE.WebGLRenderer | null {
    try {
      const renderer = new THREE.WebGLRenderer({ canvas: this.element, antialias: true });
      renderer.setClearColor(0x000000, 1);
      return renderer;
    } catch {
      return null;
    }
  }

  /** Resize the renderer + composer + camera to the stage pixel box. */
  resize(width: number, height: number): void {
    if (!this.renderer || !this.camera || !this.composer) return;
    // DPR capped at 1.5 for the cinematic (Wave 1 perf guard): with the bokeh
    // pass double-rendering the scene, 2.0 on a 4K panel drops under 20 fps —
    // heavy grain + DOF hide the resolution difference completely.
    const ratio = Math.min(globalThis.devicePixelRatio ?? 1, 1.5);
    this.renderer.setPixelRatio(ratio);
    this.renderer.setSize(width, height, false);
    this.composer.setPixelRatio(ratio);
    this.composer.setSize(width, height);
    if (this.postFx) {
      const res = this.postFx.uniforms.uResolution;
      if (res) (res.value as THREE.Vector2).set(width * ratio, height * ratio);
    }
    this.camera.aspect = width / Math.max(height, 1);
    this.camera.updateProjectionMatrix();
  }

  /** Apply the cinematic state at clock `t` and render one frame (no-op if disabled). */
  update(t: number): void {
    if (!this.renderer || !this.scene || !this.camera || !this.composer || !this.cenotaph) return;
    sampleIntroCamera24fps(this.camera, t);
    this.cenotaph.updateCinematic(t, this.camera);
    this.ash?.update(t);
    const sunOp = introSunOpacity(t) * introSunFlicker(t);
    if (this.sunSprite) this.sunSprite.material.opacity = sunOp;
    if (this.sliver) this.sliver.material.opacity = sunOp * 0.8;
    this.renderer.toneMappingExposure = introExposure(t);
    this.applyDof(t);
    if (this.postFx) {
      this.postFx.uniforms.uTime!.value = t;
      this.postFx.uniforms.uGrain!.value = introGrain(t);
    }
    this.composer.render();
  }

  /** Snap to the KF7 resting establishing pose (reduced-motion + skip handoff). */
  snapToHandoff(): void {
    if (!this.renderer || !this.camera || !this.composer || !this.cenotaph) return;
    sampleIntroCamera(this.camera, INTRO_HANDOFF_KEY.t);
    this.cenotaph.updateCinematic(INTRO_HANDOFF_KEY.t, this.camera);
    this.applyDof(INTRO_HANDOFF_KEY.t);
    this.renderer.toneMappingExposure = introExposure(INTRO_HANDOFF_KEY.t);
    this.composer.render();
  }

  private applyDof(t: number): void {
    if (!this.bokeh || !this.camera) return;
    const dist = this.camera.position.length();
    // Beat-aware focal plane: sun sprite (Ignition) → lit limb/surface (Emergence/Names)
    // → sphere centre (Marble/Title). Razor-thin at the graze, deep on the wide reveal.
    let focus: number;
    if (t < 4.5) focus = 120;
    else if (t < 17.5) focus = Math.max(dist - 70, 8);
    else focus = dist;
    const u = this.bokeh.uniforms as unknown as BokehUniforms;
    u.focus.value = focus;
    u.aperture.value = introAperture(t);
    u.maxblur.value = introMaxBlur(t);
  }

  private warmUp(): void {
    if (!this.composer) return;
    try {
      this.composer.render();
    } catch {
      /* first-frame warm-up is best-effort */
    }
  }

  /** Free every GPU resource and detach the canvas. Idempotent. */
  dispose(): void {
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
    this.ash?.dispose();
    this.ash = null;
    this.sunSprite = null;
    this.sliver = null;
    this.composer?.dispose();
    this.composer = null;
    this.bokeh = null;
    this.postFx = null;
    this.cenotaph = null;
    this.renderer?.dispose();
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.element.remove();
  }
}
